import Badge from './Badge';
import FriendListItem from './FriendListItem';
import type {FriendData, ProfileSummary} from '../types/steam';
import {getSmurfMatchType} from '../utils/dateUtils';

interface FriendsListProps {
    friends: FriendData[];
    mainProfile: ProfileSummary;
}

function FriendsList({friends, mainProfile}: FriendsListProps) {
    const vacCount = friends.filter((f) => f.bans.VACBanned).length;
    const gameCount = friends.filter((f) => f.bans.NumberOfGameBans > 0).length;
    const formatPercentage = (count: number) => `(${Math.round((count / friends.length) * 100)}%)`;
    const getSmurfRank = (friend: FriendData): number => {
        const matchType = getSmurfMatchType(friend, mainProfile);
        return matchType === 'direct' ? 2 : matchType === 'recent' ? 1 : 0;
    };

    // Sort friends: VAC bans first, then game bans, then smurfs, then alphabetically
    const sortedFriends = [...friends].sort((a, b) => {
        // VAC bans come first
        if (a.bans.VACBanned && !b.bans.VACBanned) return -1;
        if (!a.bans.VACBanned && b.bans.VACBanned) return 1;

        // Then game bans
        if (a.bans.NumberOfGameBans > 0 && b.bans.NumberOfGameBans === 0) return -1;
        if (a.bans.NumberOfGameBans === 0 && b.bans.NumberOfGameBans > 0) return 1;

        // Then potential smurfs, preferring direct name matches over recent-name matches
        const smurfRankDiff = getSmurfRank(b) - getSmurfRank(a);
        if (smurfRankDiff !== 0) return smurfRankDiff;

        // Finally alphabetically
        return a.player.personaname.localeCompare(b.player.personaname);
    });

    if (friends.length === 0) {
        return (
            <div className="empty-state">
                No friends data loaded
            </div>
        );
    }

    return (
        <>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Friends List</h2>
                <div className="flex items-center gap-2 text-sm text-muted">
                    <span>{friends.length} friends listed</span>
                    {vacCount > 0 && (
                        <Badge variant="vac">
                            {vacCount} VAC ban{vacCount !== 1 ? 's' : ''} {formatPercentage(vacCount)}
                        </Badge>
                    )}
                    {gameCount > 0 && (
                        <Badge variant="game">
                            {gameCount} Game Ban{gameCount !== 1 ? 's' : ''} {formatPercentage(gameCount)}
                        </Badge>
                    )}
                </div>
            </div>
            <ul className="friend-list-container">
                {sortedFriends.map((friend) => (
                    <FriendListItem key={friend.player.steamid}
                                    friend={friend}
                                    mainProfile={mainProfile}/>
                ))}
            </ul>
        </>
    );
}

export default FriendsList;
